import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

type SpeechRecognitionLike = any;

function getRecognitionCtor(): any | null {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

export function isMicSupported() {
  return !!getRecognitionCtor();
}

function appendTranscript(prev: string, chunk: string) {
  if (!prev) return chunk;
  const needsSpace = !/\s$/.test(prev);
  return prev + (needsSpace ? " " : "") + chunk;
}

export function MicButton({
  label,
  value,
  onChange,
  disabled = false,
  className = "",
}: {
  /** Short noun describing the field (e.g. "your thesis"). Used in tooltip. */
  label?: string;
  /** Current text value of the field being dictated into. */
  value: string;
  /** Setter for the field — called with the new full value after each chunk. */
  onChange: (newValue: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    setSupported(isMicSupported());
    return () => {
      try {
        recRef.current?.stop?.();
      } catch {
        /* noop */
      }
    };
  }, []);

  if (!supported) return null;

  const tooltip = listening
    ? "Stop dictation"
    : label
    ? `Dictate ${label}`
    : "Dictate";

  function start() {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    try {
      const rec: SpeechRecognitionLike = new Ctor();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      // Snapshot the value at start; we rebuild from this base each result so
      // interim text shows live and gets replaced as it finalizes.
      let baseValue = valueRef.current;
      let manualStop = false;
      rec.onresult = (e: any) => {
        let finalChunk = "";
        let interimChunk = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i];
          if (res.isFinal) finalChunk += res[0].transcript;
          else interimChunk += res[0].transcript;
        }
        if (finalChunk.trim()) {
          baseValue = appendTranscript(baseValue, finalChunk.trim());
        }
        const display = interimChunk.trim()
          ? appendTranscript(baseValue, interimChunk.trim())
          : baseValue;
        valueRef.current = display;
        onChange(display);
      };
      rec.onerror = (e: any) => {
        const err = e?.error || "unknown";
        if (err === "not-allowed" || err === "service-not-allowed") {
          manualStop = true;
          toast.error("Microphone access denied. Allow mic permission and try again.");
          setListening(false);
        }
        // other errors (no-speech, aborted, network) — onend will fire and we restart
      };
      rec.onend = () => {
        // Commit any pending interim as final to the base
        baseValue = valueRef.current;
        if (manualStop) {
          setListening(false);
          return;
        }
        setTimeout(() => {
          if (manualStop) return;
          try {
            rec.start();
          } catch {
            try { rec.start(); } catch { setListening(false); }
          }
        }, 50);
      };
      // expose stop via closure
      (rec as any).__manualStop = () => { manualStop = true; };
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't start microphone.");
      setListening(false);
    }
  }

  function stop() {
    try {
      (recRef.current as any)?.__manualStop?.();
      recRef.current?.stop?.();
    } catch {
      /* noop */
    }
    setListening(false);
  }

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      aria-pressed={listening}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
        listening
          ? "border-primary bg-primary text-primary-foreground shadow-glow animate-pulse"
          : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""} ${className}`}
    >
      {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
